import { ThemeOptions } from '@mui/material';

/**
 * Sentry feedback integration theme configuration
 * Derived from the application's Material UI theme colors
 */
export const sentryThemeConfig = {
  light: {
    background: '#FAFAFA',
    foreground: '#303540',
    accent: '#48A1DB',
  },
  dark: {
    background: '#353536',
    foreground: '#A3A3A3',
    accent: '#4A67FB',
  },
} as const;

/**
 * Generate Sentry theme config from MUI theme
 * Extracts palette colors from the resolved theme
 */
export function generateSentryThemeConfig(themeOptions?: ThemeOptions) {
  const defaultConfig = {
    light: {
      background: '#FAFAFA',
      foreground: '#303540',
      accent: '#48A1DB',
    },
    dark: {
      background: '#353536',
      foreground: '#A3A3A3',
      accent: '#4A67FB',
    },
  };

  if (!themeOptions?.palette) {
    return defaultConfig;
  }

  const palette = themeOptions.palette;

  return {
    light: {
      background:
        (palette.background?.default as string) ??
        defaultConfig.light.background,
      foreground:
        (palette.text?.primary as string) ?? defaultConfig.light.foreground,
      accent:
        ((palette.primary as any)?.main as string) ??
        defaultConfig.light.accent,
    },
    dark: {
      background:
        (palette.background?.paper as string) ?? defaultConfig.dark.background,
      foreground:
        (palette.text?.secondary as string) ?? defaultConfig.dark.foreground,
      accent:
        ((palette.secondary as any)?.main as string) ??
        defaultConfig.dark.accent,
    },
  };
}

export type SentryThemeConfig = ReturnType<typeof generateSentryThemeConfig>;

/**
 * Get CSS styles for custom form input field
 * Uses Sentry's CSS custom properties for theme consistency
 */
export const getSentryCustomInputStyles = () => ({
  container: `
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 0px;
  `,
  label: `
    display: block;
    font-family: var(--font-family);
    font-size: var(--font-size);
    color: var(--foreground);
    font-weight: 400;
  `,
  input: `
    font-family: var(--font-family);
    font-size: var(--font-size);
    color: var(--foreground);
    background: var(--background);
    border: var(--border);
    border-radius: 4px;
    padding: 10px;
    box-sizing: border-box;
    -webkit-appearance: none;
    -webkit-user-select: text;
    user-select: text;
    -webkit-touch-callout: default;
    outline: none;
  `,
});
